/**
 * deepLink/openSet — the web half of a friend's `swimset://set/<enc>` link
 * (contract C3; deep-link-ios record N2).
 *
 * The native sheet's primary button dispatches, into the Sets tab's web view
 * (ios/swimset/DeepLink/OpenSetBridge.swift):
 *
 *   window.dispatchEvent(new CustomEvent('swimset:open-set', { detail: "<encoded>" }))
 *
 * `detail` is the ORIGINAL codec segment, the same string as a story link's
 * <enc>, so it is read with `decodeShareCard` and nothing else. A decoded card is
 * untrusted input (C1): the codec validates it with `isShareCard`.
 *
 * Failure copy mirrors DeepLinkSheet.swift's three problems word for word, so a
 * link that fails in the sheet and in Sets says the same thing in both.
 */
import { writable } from "svelte/store";
import { decodeShareCard } from "../shareCard/codec";
import { SHARE_CARD_VERSION, type ShareSetCard } from "../shareCard/types";
import { goToTab } from "../tabNav";
import { saveMySet, type SaveResult } from "../mySets/mySets";

/** Copied from OpenSetBridge.eventName, never from prose. */
export const OPEN_SET_EVENT = "swimset:open-set";

export type OpenSetProblem = "corrupt" | "wrongKind" | "tooNew";

export type OpenSetResult =
  | { ok: true; card: ShareSetCard }
  | { ok: false; problem: OpenSetProblem };

/** DeepLinkSheet.swift `DeepLinkUnreadableView.Problem` title / hint, verbatim. */
export const OPEN_SET_PROBLEM_COPY: Record<OpenSetProblem, { title: string; hint: string }> = {
  corrupt: {
    title: "This link could not be read.",
    hint: "Ask the person who sent it for the link again.",
  },
  wrongKind: {
    title: "This link is for a different kind of card.",
    hint: "Ask the person who sent it for the link again.",
  },
  tooNew: {
    title: "Update Swim Set Club to open this link.",
    hint: "It was made by a newer version of the app. Install the latest build, then open the link again.",
  },
};

/** The native codec refuses a card whose JSON passes 1 MiB; base64url of that is
 *  about 1.4 M characters. Anything longer is not a card, whatever it holds. */
const MAX_DETAIL_CHARS = 1_400_000;

/**
 * Only for telling "too new" from "corrupt" AFTER the codec refused the segment:
 * the codec's "not a v1 card" covers both a v2 card and a broken v1 one, and
 * codec.ts is frozen (C6). Same rule as ShareCardCodec.swift: the JSON parses,
 * and its `v` is an integer above 1. Returns null whenever that cannot be read.
 */
async function peekVersion(encoded: string): Promise<number | null> {
  try {
    const gzipped = encoded.startsWith("1z.");
    if (!gzipped && !encoded.startsWith("1.")) return null;
    const b64 = encoded.slice(gzipped ? 3 : 2).replace(/-/g, "+").replace(/_/g, "/");
    const bin = atob(b64 + "=".repeat((4 - (b64.length % 4)) % 4));
    let bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
    if (gzipped) {
      if (typeof DecompressionStream === "undefined") return null;
      const ds = new DecompressionStream("gzip");
      const writer = ds.writable.getWriter();
      void writer.write(bytes).then(() => writer.close()).catch(() => {});
      const reader = ds.readable.getReader();
      const chunks: Uint8Array[] = [];
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
      }
      bytes = new Uint8Array(chunks.reduce((n, c) => n + c.length, 0));
      let at = 0;
      for (const c of chunks) {
        bytes.set(c, at);
        at += c.length;
      }
    }
    const v = (JSON.parse(new TextDecoder().decode(bytes)) as { v?: unknown }).v;
    return typeof v === "number" && Number.isInteger(v) ? v : null;
  } catch {
    return null;
  }
}

/** The event's detail, as a set card or as the one problem to show. Never throws. */
export async function readOpenSet(detail: unknown): Promise<OpenSetResult> {
  if (typeof detail !== "string" || detail.length === 0 || detail.length > MAX_DETAIL_CHARS) {
    return { ok: false, problem: "corrupt" };
  }
  try {
    const card = await decodeShareCard(detail);
    // Swim links stay read-only (deep-link N3): only a set card opens here.
    return card.kind === "set" ? { ok: true, card } : { ok: false, problem: "wrongKind" };
  } catch {
    const v = await peekVersion(detail);
    return { ok: false, problem: v !== null && v > SHARE_CARD_VERSION ? "tooNew" : "corrupt" };
  }
}

/** Listen for the native call. Every event is handled, in arrival order of its
 *  decode. Returns the remover for onMount's cleanup. */
export function installOpenSetListener(
  handle: (result: OpenSetResult) => void,
  target: EventTarget = window,
): () => void {
  const listener = (e: Event) => {
    void readOpenSet((e as CustomEvent<unknown>).detail).then(handle);
  };
  target.addEventListener(OPEN_SET_EVENT, listener);
  return () => target.removeEventListener(OPEN_SET_EVENT, listener);
}

/** What the Sets tab shows for the latest link. `at` tells two arrivals of the
 *  same link apart, so the notice re-appears (and re-scrolls) each time. */
export type OpenSetArrival = (
  | { ok: true; card: ShareSetCard; save: SaveResult }
  | { ok: false; problem: OpenSetProblem }
) & { at: number };

export const openSetArrival = writable<OpenSetArrival | null>(null);

/** The app's handler: save a set card to my sets, record the arrival, then land
 *  on Sets (a no-op under native tabs, where the shell already selected Sets
 *  before dispatching). */
export function receiveOpenSet(result: OpenSetResult): void {
  const at = Date.now();
  if (result.ok) {
    const save = saveMySet(result.card);
    openSetArrival.set({ ...result, save, at });
    console.info(
      save.ok
        ? `[swimset] open-set: "${result.card.set.name}" ${save.added ? "saved to my sets" : "already in my sets"}`
        : `[swimset] open-set: "${result.card.set.name}" not saved (${save.reason})`,
    );
  } else {
    openSetArrival.set({ ...result, at });
    console.info(`[swimset] open-set: refused (${result.problem})`);
  }
  goToTab("sets");
}
